// ─────────────────────────────────────────────
//  MessageBubble — one chat message (Phase 5 messaging / UI-SPEC "Conversation
//  View"). Extends the proven MessagesPage bubble visual language, lifted to
//  the 4-size/2-weight token contract.
//
//  • mine    → navy #1e40af fill, white text, bubbleOut corners (16 4 16 16),
//              right-aligned, no avatar.
//  • theirs  → #dde6f1 raised fill, text.primary, bubbleIn corners (4 16 16 16),
//              left-aligned, initials avatar.
//  • body 14px/1.5; timestamp beneath at 12px text.muted (sentAt may be null
//    while the serverTimestamp resolves — guarded to "…").
//  • showSender (GROUP channels only) renders the sender name 12px/700
//    text.secondary above the bubble; DMs pass showSender=false.
//
//  Threat T-05-14 (XSS): the body is rendered as React text children (no raw
//  HTML injection sink), so it is auto-escaped.
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../theme/tokens";

const { color, font, space } = tokens;

function initialsOf(name) {
  return (name || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatTime(sentAt) {
  // serverTimestamp() is null on the local optimistic snapshot until the
  // server resolves it; guard so we never throw on .toDate().
  if (sentAt && typeof sentAt.toDate === "function") {
    return sentAt
      .toDate()
      .toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  }
  return "…";
}

export default function MessageBubble({ message, mine, showSender }) {
  const body = message?.body ?? "";
  const senderName = message?.senderName || "";
  const time = formatTime(message?.sentAt);

  return (
    <div style={{ ...styles.row, ...(mine ? styles.rowMine : {}) }}>
      {!mine && (
        <div style={styles.avatar} aria-hidden="true">
          {initialsOf(senderName)}
        </div>
      )}
      <div style={styles.stack}>
        {showSender && !mine && senderName ? (
          <div style={styles.sender}>{senderName}</div>
        ) : null}
        <div style={{ ...styles.bubble, ...(mine ? styles.bubbleOut : styles.bubbleIn) }}>
          {body}
        </div>
        <div style={{ ...styles.time, textAlign: mine ? "right" : "left" }}>{time}</div>
      </div>
    </div>
  );
}

const styles = {
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: space.sm,
    marginBottom: space.md,
  },
  rowMine: {
    justifyContent: "flex-end",
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#e8edf9",
    color: color.accent.primary,
    border: `1px solid ${color.accent.primary}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: font.size.label, // 12
    fontWeight: font.weight.bold,
    flexShrink: 0,
  },
  stack: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "75%",
  },
  sender: {
    fontSize: font.size.label, // 12
    fontWeight: font.weight.bold, // 700
    color: color.text.secondary,
    marginBottom: space.xs,
  },
  bubble: {
    padding: "10px 14px",
    fontSize: font.size.body, // 14
    lineHeight: font.lineHeight.normal, // 1.5
    wordBreak: "break-word",
    whiteSpace: "pre-wrap", // preserve Shift+Enter newlines
  },
  bubbleIn: {
    background: color.surface.raised, // #dde6f1
    color: color.text.primary,
    borderRadius: "4px 16px 16px 16px",
  },
  bubbleOut: {
    background: color.accent.primary, // navy #1e40af
    color: "#fff",
    borderRadius: "16px 4px 16px 16px",
  },
  time: {
    fontSize: font.size.label, // 12 (lifted from legacy 10)
    color: color.text.muted,
    marginTop: space.xs,
  },
};
